import { useCallback } from 'react';

import type { ChatCardTone, ChatMessage } from '@karehero/models';

import { useFeatureFlag } from './useFeatureFlags';
import { saidKeys, useFeltOnNew } from './useFeltOnNew';
import { type HapticStrength, useHaptics } from './useHaptics';

export const ONBOARDING_HAPTICS_FLAG = 'useOnboardingHapticFeedback';

const SAID_STRENGTH: HapticStrength = 'light';
const TICKED_STRENGTH: HapticStrength = 'medium';

/** The checklists ticked off as they talk. */
const TICKED_TONES: readonly ChatCardTone[] = ['prompts', 'need'];

const tickedKeys = (messages: readonly ChatMessage[]) =>
  messages.flatMap(({ id, content }) =>
    content.kind === 'card' && TICKED_TONES.includes(content.tone)
      ? (content.items ?? [])
          .filter((item) => item.state === 'done')
          .map((item) => `${id}:${item.label}`)
      : [],
  );

export interface UseOnboardingHapticsOptions {
  messages: readonly ChatMessage[];
  /** Whether the session has loaded, so what is already there is not felt. */
  isReady: boolean;
  /** Whether what ticks the checklists has caught up with the session, so ticks from before are not felt. */
  isTicksSettled: boolean;
}

/**
 * useOnboardingHaptics taps as Keni says something and as an item is ticked
 * off while they talk.
 */
export const useOnboardingHaptics = ({
  messages,
  isReady,
  isTicksSettled,
}: UseOnboardingHapticsOptions) => {
  const isEnabled = !!useFeatureFlag(ONBOARDING_HAPTICS_FLAG);
  const { impact } = useHaptics({ isEnabled });
  const feelSaid = useCallback(() => impact(SAID_STRENGTH), [impact]);
  const feelTicked = useCallback(() => impact(TICKED_STRENGTH), [impact]);

  useFeltOnNew(saidKeys(messages), { isReady, isSettled: true }, feelSaid);
  useFeltOnNew(
    tickedKeys(messages),
    { isReady, isSettled: isTicksSettled },
    feelTicked,
  );
};
